import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import { CHANNELS, compareChannels } from '@/models/dashboards/channels.enum';
import {
  getMetricMediaType,
  getMediaTypeMetricParentName,
} from '@/app/dashboards/utils/media-types.utils';
import { getMetricText } from '@/models/dashboards/metrics';
import { REPORTS } from '@/app/dashboards/utils/reports.enum';
import { formatToIndividualCompetitorTags } from '@/app/dashboards/utils/tagging.utils';
import { extractReportType } from '@/app/dashboards/utils/dashboard-general.util';
import { extractBenchmarksObjectFromIndustries } from '@/app/dashboards/utils/benchmarks.utils';
import { getMetricDetails } from '@/utils/metrics';
import { useMetricsStore } from '@/stores/metrics';
import {
  CHANNELS_WITH_MEDIA_TYPE_BREAKDOWN_SUPPORT,
  METRIC_TYPE_OPTIONS,
} from '@/app/dashboards/constants';
import { DATA_TYPES } from '@/models/dashboards/metrics.constants';
import { useSearchStore } from '@/stores/search';
import { useContentTagsStore } from '@/stores/content-tags';
import { useCustomMetricsStore } from '@/stores/custom-metrics';
import { useDashboardsSocialListeningStore } from '@/stores/dashboards-social-listening';

export function prefixObjectProperties(value, prefix) {
  return Object.keys(value || {}).reduce((results, prop) => {
    const newProp = camelCase(`${prefix}_${prop}`);
    if (newProp === 'reportChannels') {
      const newPropAlt = 'reportChannel';
      const sortedChannelList = [...value[prop]].sort(compareChannels);
      results[newPropAlt] = sortedChannelList.toString();
    } else {
      results[newProp] = value[prop];
    }
    return results;
  }, {});
}

export function objectChanges(object, base) {
  return Object.keys(object || {}).reduce((acc, key) => {
    if (!isEqual(object?.[key], base?.[key])) {
      acc[key] = object[key];
    }
    return acc;
  }, {});
}

export function extractBrandNames(brands, ids) {
  return brands?.filter((brand) => ids?.includes(brand.id))?.map((brand) => brand?.name);
}

export function extractCompetitorNames(competitors, ids) {
  return competitors
    ?.filter((competitor) => ids?.includes(competitor.sourceAccountId))
    ?.map((competitor) => competitor?.handle);
}

export function contentCreator(channel) {
  return channel === CHANNELS.INSTAGRAM_UGC.value ? 'UGC' : 'Owned';
}

export function extractBrandAndCompetitorTagProperties({ props }) {
  let brandSelectionType = null;
  let competitorSelectionType = null;

  const {
    reportBrandIds,
    reportBrandTags,
    reportCompetitorSourceAccountIds,
    reportCompetitorTags,
  } = props;

  const reportBrandTagUsed = reportBrandTags?.length || 0;
  const reportCompetitorTagsUsed =
    formatToIndividualCompetitorTags(reportCompetitorTags).map(
      (competitorTag) => competitorTag.tagId,
    ).length || 0;

  if (reportBrandIds?.length > 0 || reportBrandTagUsed) {
    brandSelectionType = reportBrandTagUsed ? 'Brand Tags' : 'Brand';
  } else {
    brandSelectionType = null;
  }

  if (reportCompetitorSourceAccountIds?.length > 0 || reportCompetitorTagsUsed) {
    competitorSelectionType = reportCompetitorTagsUsed ? 'Competitor Tags' : 'Competitor';
  } else {
    competitorSelectionType = null;
  }

  return {
    brandSelectionType,
    competitorSelectionType,
    reportBrandTagUsed,
    reportCompetitorTagsUsed,
  };
}

export function extractAdsProperties({
  reportSourceAdAccountIds = [],
  reportSourceCampaignIds = [],
} = {}) {
  return {
    adAccountUsed: reportSourceAdAccountIds?.length ?? 0,
    adCampaignUsed: reportSourceCampaignIds?.length ?? 0,
  };
}

export function extractContentTagNames(contentTags, ids) {
  return contentTags
    ?.filter((contentTag) => ids?.includes(contentTag.id))
    ?.map((contentTag) => contentTag?.name);
}

export function extractFilterKeysWithValues(filters) {
  return Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0 && value.every((item) => item)) {
      acc.push({ key, value });
    }
    return acc;
  }, []);
}

export function extractGoalProperties(goals) {
  if (!goals?.overall) return null;
  const { value, type } = goals.overall;
  if (type === DATA_TYPES.PERCENTAGE) return `${value}%`;
  if (type === DATA_TYPES.MILLISECONDS) {
    return `${value?.hours ?? 0}hours, ${value?.minutes ?? 0}minutes, ${value?.seconds ?? 0}seconds`;
  }
  return value;
}

export function extractTopicsAndComparisons({
  topicIds = [],
  comparisonTopicIds = [],
  topics = [],
  comparisonTopics = [],
}) {
  if (!topicIds?.length && !comparisonTopicIds?.length)
    return {
      reportTopicsUsed: [],
      reportComparativeTopicsUsed: [],
      numberOfReportTopics: 0,
      numberOfReportComparativeTopics: 0,
    };

  const reportTopics = topics?.filter((topic) => topicIds?.includes(topic?.id));
  const reportComparativeTopics = comparisonTopics?.filter((comparison) =>
    comparisonTopicIds?.includes(comparison?.id),
  );
  return {
    reportTopicsUsed: reportTopics?.map((topic) => topic?.name),
    reportComparativeTopicsUsed: reportComparativeTopics?.map((comparison) => comparison?.name),
    numberOfReportTopics: reportTopics?.length,
    numberOfReportComparativeTopics: reportComparativeTopics?.length,
  };
}

export function extractMessageTags({ messageTagIds = [], messageTagFilterOptions = [] }) {
  if (!messageTagIds?.length) return { reportMessageTagsUsed: [], numberOfReportMessageTags: 0 };

  const reportMessageTagsUsed = messageTagFilterOptions.reduce((acc, option) => {
    const messageTags = option.children
      .filter((child) => messageTagIds.includes(child.value || child.id))
      .map((messageTag) => messageTag.name);
    return acc.concat(messageTags);
  }, []);

  return { reportMessageTagsUsed, numberOfReportMessageTags: reportMessageTagsUsed.length };
}

export function getBaseReportProperties(
  reportId,
  reportType,
  reportMeta,
  dashboardsStore,
  messageTagFilterOptions = [],
  previousReport = false,
) {
  const searchStore = useSearchStore();
  const contentTagsStore = useContentTagsStore();
  const customMetricsStore = useCustomMetricsStore();
  const dashboardsSocialListeningStore = useDashboardsSocialListeningStore();

  const competitors = searchStore.competitors;
  const contentTags = contentTagsStore.allOrganizationsContentTags;
  const customMetricList = customMetricsStore.customMetricList.data;
  const topics = dashboardsSocialListeningStore.topicListForMultipleOrganizations;
  const comparisonTopics = dashboardsSocialListeningStore.comparisonListForMultipleOrganizations;

  const brands = reportMeta?.brand_ids || [];
  const channels = reportMeta?.channels || (reportMeta?.channel ? [reportMeta?.channel] : []);
  const reportMetrics = reportMeta?.metrics || (reportMeta?.metric ? [reportMeta?.metric] : []);
  const baseMetric = getMediaTypeMetricParentName(reportMetrics?.[0], channels, reportType);
  const reportChannel = channels?.length === 1 ? channels[0] : null;
  const benchmarks = reportMeta?.benchmarks || [];
  const numberOfReportCompetitors = reportMeta?.competitor_source_account_ids?.length || [];
  const metricDetails = getMetricDetails(
    baseMetric,
    channels,
    REPORTS[reportType].metricTypeReportKey,
  );

  const isCustomMetric = reportMetrics?.[0]?.startsWith('CM_');
  const customMetricId = isCustomMetric ? reportMetrics?.[0].split('CM_')[1] : null;
  const customMetric = customMetricList?.find((metric) => metric?.id === Number(customMetricId));

  const metricsStore = useMetricsStore();
  const metricsStoreMetricDetails = metricsStore.getTargetMetricDetails(
    reportChannel,
    REPORTS[reportType]?.metricTypeReportKey,
    baseMetric,
  );
  const reportMetricText = isCustomMetric
    ? customMetric.name
    : (metricDetails?.displayName ?? getMetricText(channels[0], baseMetric, channels));

  const reportMetricType = isCustomMetric
    ? METRIC_TYPE_OPTIONS.CUSTOM_METRICS
    : METRIC_TYPE_OPTIONS.PLATFORM_METRICS;

  const userSelectedMediaTypes = Boolean(
    CHANNELS_WITH_MEDIA_TYPE_BREAKDOWN_SUPPORT.includes(reportChannel) &&
      metricsStoreMetricDetails?.supports_media_breakdown &&
      !metricsStoreMetricDetails?.locked_media_breakdown,
  );

  const mediaTypes = reportMetrics.reduce((result, metric) => {
    if (userSelectedMediaTypes) {
      const mediaTypeObj = getMetricMediaType(
        reportChannel,
        metric,
        REPORTS[reportType].metricTypeReportKey,
      );
      result.push(mediaTypeObj.text);
    }
    return result;
  }, []);

  const reportBrandNames = extractBrandNames(
    dashboardsStore.dashboardIdentityBrands,
    reportMeta?.brand_ids,
  );
  const reportCompetitorNames = extractCompetitorNames(
    competitors[CHANNELS.INSTAGRAM.value],
    reportMeta?.competitor_source_account_ids,
  );
  const reportBenchmarksNames = extractBenchmarksObjectFromIndustries(
    benchmarks,
    dashboardsStore.industries,
  ).map((benchmark) => benchmark.name.replace(':', ' -'));

  const reportFilters = extractFilterKeysWithValues(
    reportMeta?.community?.filters ?? reportMeta?.listening?.filters ?? {},
  );
  const reportFiltersApplied = reportFilters?.map((item) => item?.key);
  const numberOfReportFilters = reportFilters?.length ?? 0;

  if (!previousReport) {
    return {
      dashboardId: dashboardsStore.currentDashboardId,
      dashboardName: dashboardsStore.currentDashboardName,
      reportId,
      reportType: extractReportType(reportType, brands, reportMetrics, channels, isCustomMetric),
      ...prefixObjectProperties(reportMeta, 'report'),
      reportMetric: baseMetric,
      reportMetricType,
      reportMetricDataType: metricDetails?.dataType ?? null,
      reportMetricText,
      reportMetricsMediaType: userSelectedMediaTypes ? reportMetrics : [],
      mediaType: mediaTypes,
      reportBrandNames,
      reportCompetitorNames,
      reportBenchmarksNames,
      numberOfReportBenchmarks: benchmarks?.length,
      numberOfReportChannels: channels.length,
      numberOfReportContentTags: reportMeta?.content_tag_ids?.length ?? 0,
      numberOfReportCompetitors,
      numberOfReportCreators: reportMeta?.creator_ids?.length ?? 0,
      numberOfReportCreatorsTags: reportMeta?.creator_tag_ids?.length ?? 0,
      numberOfReportFilters,
      reportFiltersApplied,
      reportOnAllContent: !reportMeta?.content_tag_ids,
      chartType: REPORTS?.[reportType]?.text || null,
      contentTagsUsed: extractContentTagNames(contentTags, reportMeta.content_tag_ids),
      ...extractBrandAndCompetitorTagProperties({
        props: { ...prefixObjectProperties(reportMeta, 'report') },
      }),
      ...extractAdsProperties(prefixObjectProperties(reportMeta, 'report')),
      ...extractTopicsAndComparisons({
        topicIds: reportMeta?.listening?.topic_ids ?? [],
        comparisonTopicIds: reportMeta?.listening?.comparison_topic_ids ?? [],
        topics,
        comparisonTopics,
      }),
      ...extractMessageTags({
        messageTagFilterOptions,
        messageTagIds: reportMeta?.community?.filters?.message_tag_ids ?? [],
      }),
      numberOfReportBrands: brands.length,
      customizationMode: dashboardsStore.dashboardCustomizeMode,
      reportGoalValue: extractGoalProperties(reportMeta?.goals),
      reportDisplayMetricAs: reportMeta?.display_strategy ?? null,
    };
  }

  return {
    customizationMode: dashboardsStore.dashboardCustomizeMode,
    previousReportMetric: baseMetric,
    previousReportMetricText:
      metricDetails?.displayName ?? getMetricText(channels[0], baseMetric, channels),
    previousReportMetricsMediaType: userSelectedMediaTypes ? reportMetrics : [],
    previousReportMediaType: mediaTypes,
    ...prefixObjectProperties(reportMeta, 'previousReport'),
  };
}
